import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Trophy, TrendingUp, Filter, GraduationCap, MapPin, Phone, Mail, User, LogIn, LogOut, Users, HelpCircle, Info, Menu, X, Home, BookOpen, Zap, FileText, Star, Video, CheckCircle, Sparkles } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EnhancedHome = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    examType: 'JEE Main',
    rank: '',
    counselling: 'JoSAA',
    homeState: '',
    category: 'General',
    gender: 'Male',
    speciallyAbled: 'No',
    mobile: '',
    name: '',
    city: '',
    email: '',
    referralCode: ''
  });

  // Counselling round state
  const [showRoundSelector, setShowRoundSelector] = useState(false);
  const [counsellingRound, setCounsellingRound] = useState(1);

  const [otpModal, setOtpModal] = useState({ open: false, type: '', value: '' });
  const [otp, setOtp] = useState('');
  const [verified, setVerified] = useState({ mobile: false, email: false });
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [existingUserData, setExistingUserData] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [verificationSettings, setVerificationSettings] = useState({
    mobile_otp_required: true,
    email_otp_required: true,
    allow_skip_verification: false,
    verification_message: "Please verify your mobile and email to continue"
  });

  // Check if user is logged in
  const isLoggedIn = localStorage.getItem('userToken');
  const userName = localStorage.getItem('userName');

  // Pre-fill form for logged-in users
  React.useEffect(() => {
    if (isLoggedIn) {
      const savedName = localStorage.getItem('userName') || '';
      const savedEmail = localStorage.getItem('userEmail') || '';
      const savedMobile = localStorage.getItem('userMobile') || '';
      
      // Only pre-fill if we have saved data
      if (savedName || savedEmail || savedMobile) {
        setFormData(prev => ({
          ...prev,
          name: savedName || prev.name,
          email: savedEmail || prev.email,
          mobile: savedMobile || prev.mobile
        }));
        
        // If user has verified contact info, mark as verified
        if (savedMobile) {
          setVerified(prev => ({ ...prev, mobile: true }));
        }
        if (savedEmail) {
          setVerified(prev => ({ ...prev, email: true }));
        }
      }
    }
  }, [isLoggedIn]);

  // Track site visit and fetch payment settings
  React.useEffect(() => {
    // Track visit
    fetch(`${BACKEND_URL}/api/analytics/visit?page=home`, { method: 'POST' }).catch(() => {});
    
    // Fetch payment settings
    fetch(`${BACKEND_URL}/api/payment/settings`)
      .then(res => res.json())
      .then(data => setPaymentSettings(data))
      .catch(() => {});
    
    // Fetch verification settings
    fetch(`${BACKEND_URL}/api/verification/settings`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setVerificationSettings(data.settings);
        }
      })
      .catch(() => {});
  }, []);

  const handleSendOTP = async (type) => {
    const value = type === 'mobile' ? formData.mobile : formData.email;
    if (!value) {
      toast({ title: "Error", description: `Please enter ${type}`, variant: "destructive" });
      return;
    }

    // Call backend API to send OTP
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          type: type, 
          value: value 
        })
      });
      const data = await response.json();
      
      if (data.success) {
        setOtpModal({ open: true, type, value });
        toast({ 
          title: "OTP Sent", 
          description: data.message || `OTP sent to ${value}` 
        });
      } else {
        toast({ 
          title: "Error", 
          description: data.message || "Failed to send OTP", 
          variant: "destructive" 
        });
      }
    } catch (error) {
      console.error('Send OTP error:', error);
      toast({ 
        title: "Error", 
        description: "Failed to send OTP. Please try again.", 
        variant: "destructive" 
      });
    }
  };

  const handleVerifyOTP = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          type: otpModal.type,
          value: otpModal.value,
          otp: otp 
        })
      });
      const data = await response.json();
      
      if (data.success) {
        setVerified({ ...verified, [otpModal.type]: true });
        setOtpModal({ open: false, type: '', value: '' });
        setOtp('');
        toast({ title: "Success", description: `${otpModal.type} verified successfully` });
      } else {
        toast({ 
          title: "Error", 
          description: data.message || "Incorrect OTP", 
          variant: "destructive" 
        });
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      toast({ 
        title: "Error", 
        description: "Failed to verify OTP. Please try again.", 
        variant: "destructive" 
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.rank) {
      toast({ 
        title: "Error", 
        description: "Please enter your rank",
        variant: "destructive"
      });
      return;
    }
    
    // If not logged in, check if user exists BEFORE verification check
    if (!isLoggedIn) {
      // Check if user exists with email or mobile
      if (formData.email || formData.mobile) {
        try {
          const response = await fetch(`${BACKEND_URL}/api/auth/check-user`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              email: formData.email, 
              mobile: formData.mobile 
            })
          });
          
          const data = await response.json();
          console.log('User check response:', data);
          
          if (data.exists) {
            // User exists, show login modal
            setExistingUserData(data);
            setLoginData({ ...loginData, email: data.email });
            setShowLoginModal(true);
            return;
          }
        } catch (error) {
          console.error('Error checking user:', error);
        }
      }
      
      // For new users, check verification requirements based on settings
      if (!verificationSettings.allow_skip_verification) {
        const mobileRequired = verificationSettings.mobile_otp_required && !verified.mobile;
        const emailRequired = verificationSettings.email_otp_required && !verified.email;
        
        if (mobileRequired || emailRequired) {
          let missingVerifications = [];
          if (mobileRequired) missingVerifications.push('mobile');
          if (emailRequired) missingVerifications.push('email');
          
          toast({ 
            title: "Verification Required", 
            description: verificationSettings.verification_message || `Please verify ${missingVerifications.join(' and ')}`, 
            variant: "destructive" 
          });
          return;
        }
      }
      
      setShowLoginPrompt(true);
      return;
    }

    // Ensure home state is synced with counselling type before navigation
    let finalFormData = { ...formData };
    if (counsellingToState[formData.counselling]) {
      finalFormData.homeState = counsellingToState[formData.counselling];
    }
    
    // Add counselling round to form data
    finalFormData.counsellingRound = showRoundSelector ? counsellingRound : 1;

    // Capture lead data
    try {
      await fetch(`${BACKEND_URL}/api/leads/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          mobile: formData.mobile,
          email: formData.email,
          rank: parseInt(formData.rank),
          exam_type: formData.examType || 'JEE Main',
          category: formData.category,
          gender: formData.gender,
          home_state: finalFormData.homeState,
          counselling: formData.counselling,
          counselling_round: finalFormData.counsellingRound,
          is_specially_abled: formData.isSpeciallyAbled || false,
          city: formData.city || '',
          source: 'prediction_form'
        })
      });
    } catch (error) {
      console.log('Lead capture error:', error);
    }

    // Check if payment page should be skipped
    if (paymentSettings?.skip_payment_page) {
      // Go directly to results page
      navigate('/results', { state: { ...finalFormData, paymentConfirmed: true } });
    } else {
      // Navigate to payment page
      navigate('/payment', { state: finalFormData });
    }
  };

  const handleLoginSubmit = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: loginData.email,
          password: loginData.password
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Store all user data correctly
        localStorage.setItem('userToken', data.token || 'user_token_' + Date.now());
        localStorage.setItem('userName', data.name);
        localStorage.setItem('userEmail', data.email);
        localStorage.setItem('userId', data.id); // Changed from data.user_id to data.id
        localStorage.setItem('userReferralCode', data.referral_code || '');
        localStorage.setItem('userEarnings', data.earnings || 0);
        
        toast({
          title: "Login Successful",
          description: `Welcome back, ${data.name}!`
        });
        
        setShowLoginModal(false);
        
        // Use navigate for smoother transition and ensure localStorage is committed
        setTimeout(() => {
          navigate('/my-predictions');
        }, 100);
      } else {
        toast({
          title: "Login Failed",
          description: data.detail || "Invalid credentials",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: "Error",
        description: "Failed to login. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleProceedWithoutLogin = () => {
    setShowLoginPrompt(false);
    // Ensure home state is synced with counselling type before navigation
    let finalFormData = { ...formData };
    if (counsellingToState[formData.counselling]) {
      finalFormData.homeState = counsellingToState[formData.counselling];
    }
    
    // Add counselling round to form data
    finalFormData.counsellingRound = showRoundSelector ? counsellingRound : 1;
    
    // Check if payment page should be skipped
    if (paymentSettings?.skip_payment_page) {
      navigate('/results', { state: { ...finalFormData, paymentConfirmed: true } });
    } else {
      navigate('/payment', { state: finalFormData });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    toast({ title: "Logged out", description: "You have been logged out successfully" });
    window.location.reload();
  };

  const states = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
    "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jammu and Kashmir",
    "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
    "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Puducherry", "Punjab", "Rajasthan",
    "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh",
    "Uttarakhand", "West Bengal"
  ];

  // All counselling options for JEE Main
  const allCounsellings = [
    "JoSAA", "CSAB", 
    // State-specific counsellings
    "MP B.E Admission", "JAC Delhi", "Haryana Admission",
    "Uttarakhand Admission", "UPTAC", "Maharashtra CAP", "IPU CET",
    "BCECE", "OJEE", "JCECE", "AP EAMCET", "CG PET", "Assam CEE",
    // New state counsellings
    "Goa CET", "HP CET", "JKCET", "CENTAC",
    "Tripura JEE", "Meghalaya JEE", "Manipur JEE", "Mizoram JEE",
    "Nagaland JEE", "Arunachal JEE", "Sikkim JEE",
    "IIIT Hyderabad B.Tech Admission", "State Counselling"
  ];

  // JEE Advanced only uses JoSAA (IITs only participate in JoSAA)
  const jeeAdvancedCounsellings = ["JoSAA"];

  // Get counsellings based on exam type
  const counsellings = formData.examType === 'JEE Advanced' 
    ? jeeAdvancedCounsellings 
    : allCounsellings;

  // Mapping of counselling types to their corresponding states
  const counsellingToState = {
    "MP B.E Admission": "Madhya Pradesh",
    "JAC Delhi": "Delhi",
    "Haryana Admission": "Haryana",
    "Uttarakhand Admission": "Uttarakhand",
    "UPTAC": "Uttar Pradesh",
    "Maharashtra CAP": "Maharashtra",
    "IPU CET": "Delhi",
    "BCECE": "Bihar",
    "OJEE": "Odisha",
    "JCECE": "Jharkhand",
    "AP EAMCET": "Andhra Pradesh",
    "CG PET": "Chhattisgarh",
    "Assam CEE": "Assam",
    "Goa CET": "Goa",
    "HP CET": "Himachal Pradesh",
    "JKCET": "Jammu and Kashmir",
    "CENTAC": "Puducherry",
    "Tripura JEE": "Tripura",
    "Meghalaya JEE": "Meghalaya",
    "Manipur JEE": "Manipur",
    "Mizoram JEE": "Mizoram",
    "Nagaland JEE": "Nagaland",
    "Arunachal JEE": "Arunachal Pradesh",
    "Sikkim JEE": "Sikkim",
    "IIIT Hyderabad B.Tech Admission": "Telangana"
  };

  // Handle counselling change - auto-set state for state-specific counsellings
  const handleCounsellingChange = (value) => {
    const newFormData = { ...formData, counselling: value };
    
    // If it's a state-specific counselling, auto-set the home state
    if (counsellingToState[value]) {
      newFormData.homeState = counsellingToState[value];
    }
    
    setFormData(newFormData);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-lg flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-base sm:text-xl font-bold text-slate-900">College Kampus</h1>
                  <p className="text-[10px] sm:text-xs text-slate-600 hidden sm:block">JEE College Predictor</p>
                </div>
              </div>
              
              {/* Navigation Links - Desktop */}
              <nav className="hidden lg:flex gap-4">
                <button 
                  onClick={() => window.location.href = '/'}
                  className="text-slate-700 hover:text-blue-600 font-medium transition text-sm"
                >
                  Home
                </button>
                <button 
                  onClick={() => navigate('/guide')}
                  className="text-slate-700 hover:text-blue-600 font-medium transition flex items-center gap-1 text-sm"
                >
                  <HelpCircle className="w-4 h-4" /> Guide
                </button>
                <button 
                  onClick={() => navigate('/test-series')}
                  className="text-slate-700 hover:text-blue-600 font-medium transition flex items-center gap-1 text-sm"
                >
                  <span className="text-yellow-500">⚡</span> Test Series
                </button>
                <button 
                  onClick={() => navigate('/about-us')}
                  className="text-slate-700 hover:text-blue-600 font-medium transition text-sm"
                >
                  About Us
                </button>
                <button 
                  onClick={() => navigate('/expert-counseling')}
                  className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1.5 rounded-full font-medium transition text-sm hover:from-orange-600 hover:to-red-600 flex items-center gap-1"
                >
                  <Video className="w-4 h-4" />
                  Expert Counseling
                </button>
              </nav>
            </div>
            
            {/* Right side buttons */}
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Mobile Hamburger Menu */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="sm" className="lg:hidden p-2" data-testid="mobile-menu-btn">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[280px] sm:w-[320px]">
                  <div className="flex flex-col gap-4 mt-6">
                    <div className="flex items-center gap-2 mb-4 pb-4 border-b">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-lg flex items-center justify-center">
                        <GraduationCap className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h2 className="font-bold text-slate-900">College Kampus</h2>
                        <p className="text-xs text-slate-600">JEE College Predictor</p>
                      </div>
                    </div>
                    
                    <nav className="flex flex-col gap-2">
                      <Button 
                        variant="ghost" 
                        className="w-full justify-start gap-3 text-left"
                        onClick={() => { window.location.href = '/'; setMobileMenuOpen(false); }}
                      >
                        <Home className="w-5 h-5 text-blue-600" />
                        Home
                      </Button>
                      <Button 
                        variant="ghost" 
                        className="w-full justify-start gap-3 text-left"
                        onClick={() => { navigate('/guide'); setMobileMenuOpen(false); }}
                      >
                        <HelpCircle className="w-5 h-5 text-green-600" />
                        Guide
                      </Button>
                      <Button 
                        variant="ghost" 
                        className="w-full justify-start gap-3 text-left"
                        onClick={() => { navigate('/test-series'); setMobileMenuOpen(false); }}
                      >
                        <Zap className="w-5 h-5 text-yellow-500" />
                        Test Series
                      </Button>
                      <Button 
                        variant="ghost" 
                        className="w-full justify-start gap-3 text-left bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200"
                        onClick={() => { navigate('/expert-counseling'); setMobileMenuOpen(false); }}
                      >
                        <Video className="w-5 h-5 text-orange-600" />
                        <span className="flex-1">Expert Counseling</span>
                        <Badge className="bg-orange-500 text-white text-xs">NEW</Badge>
                      </Button>
                      <Button 
                        variant="ghost" 
                        className="w-full justify-start gap-3 text-left"
                        onClick={() => { navigate('/about-us'); setMobileMenuOpen(false); }}
                      >
                        <FileText className="w-5 h-5 text-purple-600" />
                        About Us
                      </Button>
                      
                      {isLoggedIn && (
                        <>
                          <div className="border-t my-2" />
                          <Button 
                            variant="ghost" 
                            className="w-full justify-start gap-3 text-left"
                            onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                          >
                            <User className="w-5 h-5 text-blue-600" />
                            Dashboard
                          </Button>
                          <Button 
                            variant="ghost" 
                            className="w-full justify-start gap-3 text-left"
                            onClick={() => { navigate('/my-predictions'); setMobileMenuOpen(false); }}
                          >
                            <TrendingUp className="w-5 h-5 text-indigo-600" />
                            My Predictions
                          </Button>
                          <Button 
                            variant="ghost" 
                            className="w-full justify-start gap-3 text-left"
                            onClick={() => { navigate('/counseling-request'); setMobileMenuOpen(false); }}
                          >
                            <Users className="w-5 h-5 text-purple-600" />
                            Get Counseling
                          </Button>
                          <Button 
                            variant="ghost" 
                            className="w-full justify-start gap-3 text-left"
                            onClick={() => { navigate('/expert-counseling'); setMobileMenuOpen(false); }}
                          >
                            <GraduationCap className="w-5 h-5 text-orange-600" />
                            Expert Sessions
                          </Button>
                          <Button 
                            variant="ghost" 
                            className="w-full justify-start gap-3 text-left"
                            onClick={() => { navigate('/referral'); setMobileMenuOpen(false); }}
                          >
                            <Trophy className="w-5 h-5 text-green-600" />
                            Referrals
                          </Button>
                        </>
                      )}
                    </nav>
                    
                    <div className="border-t pt-4 mt-auto">
                      {isLoggedIn ? (
                        <Button 
                          variant="outline" 
                          className="w-full gap-2"
                          onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                        >
                          <LogOut className="w-4 h-4" />
                          Logout ({userName})
                        </Button>
                      ) : (
                        <Button 
                          className="w-full gap-2"
                          onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                        >
                          <LogIn className="w-4 h-4" />
                          Login / Register
                        </Button>
                      )}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
              
              {isLoggedIn ? (
                <>
                  {/* Logged in: Clean navigation with dropdown for user actions */}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate('/dashboard')} 
                    className="gap-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                    data-testid="dashboard-btn"
                  >
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline">Dashboard</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate('/referral')} 
                    className="hidden md:flex gap-2 bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                  >
                    <Trophy className="w-4 h-4" />
                    Referrals
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleLogout} className="text-xs sm:text-sm">
                    <span className="hidden sm:inline">Logout ({userName})</span>
                    <LogOut className="w-4 h-4 sm:hidden" />
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" onClick={() => navigate('/login')} className="gap-1 sm:gap-2">
                  <LogIn className="w-4 h-4" />
                  <span className="hidden sm:inline">Login</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Test Series Promotion Banner */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 text-white py-3">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <span className="text-yellow-300 text-xl">⚡</span>
            <span className="font-medium">NEW: JEE Ranker Booster Series - 500+ Practice Questions & Mock Tests</span>
            <Button 
              onClick={() => navigate('/test-series')}
              size="sm"
              className="bg-white text-blue-600 hover:bg-blue-50 font-semibold"
            >
              Start Free Trial →
            </Button>
          </div>
        </div>
      </div>

      {/* Expert Counseling Promotion Banner */}
      <div className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 text-white py-3">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <span className="text-yellow-300 text-xl">🎓</span>
            <span className="font-medium">1-on-1 Expert Counseling: Get personalized IIT/NIT guidance from ₹299</span>
            <Button 
              onClick={() => navigate('/expert-counseling')}
              size="sm"
              className="bg-white text-red-600 hover:bg-red-50 font-semibold"
            >
              Book Expert Session →
            </Button>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 md:py-20">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Predict Your College Admission
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Get accurate predictions for IITs, NITs, IIITs, and other top engineering colleges based on your JEE rank with AI-powered analysis
          </p>
        </div>

        {/* Enhanced Predictor Form */}
        <Card className="max-w-4xl mx-auto shadow-xl border-slate-200">
          <CardHeader>
            <CardTitle className="text-2xl">Enter Your Details</CardTitle>
            <CardDescription>Fill in all details to get personalized predictions with PDF report via email</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Row 1: Exam Type and Counselling */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="examType" className="text-base font-medium">Exam Type *</Label>
                  <Select value={formData.examType} onValueChange={(value) => {
                    // When switching to JEE Advanced, reset counselling to JoSAA
                    const newCounselling = value === 'JEE Advanced' ? 'JoSAA' : formData.counselling;
                    setFormData({...formData, examType: value, counselling: newCounselling});
                  }}>
                    <SelectTrigger className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="JEE Main">JEE Main</SelectItem>
                      <SelectItem value="JEE Advanced">JEE Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.examType === 'JEE Advanced' && (
                    <p className="text-xs text-blue-600">JEE Advanced is only for IITs (JoSAA counselling)</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="counselling" className="text-base font-medium">Counselling *</Label>
                  <Select value={formData.counselling} onValueChange={handleCounsellingChange}>
                    <SelectTrigger className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {counsellings.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 2: Rank and Category */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rank" className="text-base font-medium">Your Rank *</Label>
                  <Input
                    id="rank"
                    type="number"
                    placeholder="Enter your JEE rank"
                    value={formData.rank}
                    onChange={(e) => setFormData({...formData, rank: e.target.value})}
                    className="h-11"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category" className="text-base font-medium">Category *</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                    <SelectTrigger className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="General">General</SelectItem>
                      <SelectItem value="EWS">Economically Weaker Section (EWS)</SelectItem>
                      <SelectItem value="OBC-NCL">OBC-NCL</SelectItem>
                      <SelectItem value="SC">SC</SelectItem>
                      <SelectItem value="ST">ST</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 3: Home State and Gender */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="homeState" className="text-base font-medium">Home State *</Label>
                  <Select value={formData.homeState} onValueChange={(value) => setFormData({...formData, homeState: value})}>
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select your home state" />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gender" className="text-base font-medium">Gender *</Label>
                  <Select value={formData.gender} onValueChange={(value) => setFormData({...formData, gender: value})}>
                    <SelectTrigger className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 4: Specially Abled */}
              <div className="space-y-2">
                <Label htmlFor="speciallyAbled" className="text-base font-medium">Are you Specially Abled? *</Label>
                <Select value={formData.speciallyAbled} onValueChange={(value) => setFormData({...formData, speciallyAbled: value})}>
                  <SelectTrigger className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="No">No</SelectItem>
                    <SelectItem value="Yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Row 4.5: Counselling Round Selection */}
              <div className="space-y-3 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-100">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="roundToggle" className="text-base font-medium flex items-center gap-2">
                      🎯 Select Counselling Round
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-4 h-4 text-blue-500 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs p-3 bg-slate-900 text-white">
                            <p className="font-semibold mb-1">How Rounds Work:</p>
                            <ul className="text-xs space-y-1">
                              <li>• <strong>Round 1:</strong> Most competitive, strictest cutoffs</li>
                              <li>• <strong>Rounds 2-5:</strong> Cutoffs relax as seats fill up</li>
                              <li>• <strong>Round 6:</strong> Spot round, most relaxed cutoffs (~25% higher)</li>
                            </ul>
                            <p className="text-xs mt-2 text-blue-300">Example: If Round 1 cutoff is 5000, Round 6 might accept up to rank 6250</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </Label>
                    <p className="text-xs text-slate-500">
                      Enable to see predictions for a specific counselling round
                    </p>
                  </div>
                  <Switch
                    id="roundToggle"
                    checked={showRoundSelector}
                    onCheckedChange={setShowRoundSelector}
                    data-testid="round-toggle"
                  />
                </div>
                
                {showRoundSelector && (
                  <div className="space-y-2 pt-2 border-t border-blue-100">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="counsellingRound" className="text-sm font-medium">
                        Counselling Round
                      </Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-sm p-3 bg-slate-900 text-white">
                            <p className="font-semibold mb-2">Cutoff Relaxation by Round:</p>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                              <span>Round 1:</span><span className="text-green-400">Base cutoff</span>
                              <span>Round 2:</span><span className="text-green-300">+5% relaxed</span>
                              <span>Round 3:</span><span className="text-yellow-300">+10% relaxed</span>
                              <span>Round 4:</span><span className="text-yellow-400">+15% relaxed</span>
                              <span>Round 5:</span><span className="text-orange-400">+20% relaxed</span>
                              <span>Round 6:</span><span className="text-orange-500">+25% relaxed</span>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Select 
                      value={String(counsellingRound)} 
                      onValueChange={(value) => setCounsellingRound(parseInt(value))}
                    >
                      <SelectTrigger className="h-11 bg-white" data-testid="round-select">
                        <SelectValue placeholder="Select Round" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Round 1 (Most Competitive)</SelectItem>
                        <SelectItem value="2">Round 2</SelectItem>
                        <SelectItem value="3">Round 3</SelectItem>
                        <SelectItem value="4">Round 4</SelectItem>
                        <SelectItem value="5">Round 5</SelectItem>
                        <SelectItem value="6">Round 6 (Spot Round)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-blue-600">
                      💡 Round 1 has strictest cutoffs. Later rounds are more relaxed as seats get filled.
                    </p>
                  </div>
                )}
              </div>

              {/* Row 5: Mobile Number with OTP */}
              <div className="space-y-2">
                <Label htmlFor="mobile" className="text-base font-medium">
                  Mobile Number {verificationSettings.mobile_otp_required && !verificationSettings.allow_skip_verification ? '*' : ''}
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="mobile"
                    type="tel"
                    placeholder="Enter 10-digit mobile number"
                    value={formData.mobile}
                    onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                    className="h-11 flex-1"
                    required={verificationSettings.mobile_otp_required && !verificationSettings.allow_skip_verification}
                    disabled={verified.mobile}
                  />
                  {verificationSettings.mobile_otp_required && !verificationSettings.allow_skip_verification && (
                    !verified.mobile ? (
                      <Button type="button" onClick={() => handleSendOTP('mobile')} className="h-11">
                        <Phone className="w-4 h-4 mr-2" />Send OTP
                      </Button>
                    ) : (
                      <Button type="button" disabled className="h-11 bg-green-600">Verified ✓</Button>
                    )
                  )}
                </div>
                {!verificationSettings.mobile_otp_required && !verificationSettings.allow_skip_verification && (
                  <p className="text-xs text-slate-500">Mobile verification is optional</p>
                )}
              </div>

              {/* Row 6: Name and City */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-base font-medium">Full Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3.5 w-4 h-4 text-slate-500" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="h-11 pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city" className="text-base font-medium">City *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3.5 w-4 h-4 text-slate-500" />
                    <Input
                      id="city"
                      type="text"
                      placeholder="Enter your city"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      className="h-11 pl-10"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Row 7: Email with OTP */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-base font-medium">
                  Email Address {verificationSettings.email_otp_required && !verificationSettings.allow_skip_verification ? '*' : ''}
                </Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 top-3.5 w-4 h-4 text-slate-500" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="h-11 pl-10"
                      required={verificationSettings.email_otp_required && !verificationSettings.allow_skip_verification}
                      disabled={verified.email}
                    />
                  </div>
                  {verificationSettings.email_otp_required && !verificationSettings.allow_skip_verification && (
                    !verified.email ? (
                      <Button type="button" onClick={() => handleSendOTP('email')} className="h-11">
                        <Mail className="w-4 h-4 mr-2" />Send OTP
                      </Button>
                    ) : (
                      <Button type="button" disabled className="h-11 bg-green-600">Verified ✓</Button>
                    )
                  )}
                </div>
                {!verificationSettings.email_otp_required && !verificationSettings.allow_skip_verification && (
                  <p className="text-xs text-slate-500">Email verification is optional</p>
                )}
              </div>

              {/* Row 8: Referral Code (Optional) */}
              <div className="space-y-2">
                <Label htmlFor="referralCode" className="text-base font-medium">Referral Code (Optional)</Label>
                <Input
                  id="referralCode"
                  type="text"
                  placeholder="Enter referral code to get additional benefits"
                  value={formData.referralCode}
                  onChange={(e) => setFormData({...formData, referralCode: e.target.value})}
                  className="h-11"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 text-base bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 transition-all duration-300"
              >
                {paymentSettings?.submit_button_text || 'Proceed to Payment'}
              </Button>

              <p className="text-xs text-slate-600 text-center">
                By proceeding, you agree to our Terms & Conditions and Privacy Policy
              </p>
            </form>
          </CardContent>
        </Card>
      </section>

      {/* Expert Counseling CTA Section */}
      <section className="container mx-auto px-4 py-12">
        <Card className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 text-white border-none overflow-hidden">
          <CardContent className="p-8 md:p-12 relative">
            {/* Background decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-32 translate-x-32" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-24 -translate-x-24" />
            
            <div className="max-w-5xl mx-auto relative z-10">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <Badge className="bg-yellow-400 text-yellow-900 mb-4">
                    <Sparkles className="w-3 h-3 mr-1" /> Premium Service
                  </Badge>
                  <h3 className="text-3xl md:text-4xl font-bold mb-4">
                    1-on-1 Expert Counseling
                  </h3>
                  <p className="text-lg mb-6 opacity-90">
                    Get personalized guidance from IIT/NIT alumni and experienced JEE counselors. 
                    Make the right college choice with expert advice.
                  </p>
                  
                  <div className="flex flex-wrap gap-4 mb-6">
                    <div className="flex items-center gap-2 bg-white/20 rounded-full px-4 py-2">
                      <Star className="w-4 h-4 text-yellow-300" />
                      <span className="text-sm">4.8+ Rating</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white/20 rounded-full px-4 py-2">
                      <Users className="w-4 h-4" />
                      <span className="text-sm">1500+ Sessions</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white/20 rounded-full px-4 py-2">
                      <CheckCircle className="w-4 h-4 text-green-300" />
                      <span className="text-sm">Money Back Guarantee</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 flex-wrap">
                    <Button 
                      size="lg"
                      onClick={() => navigate('/expert-counseling')}
                      className="gap-2 bg-white text-red-600 hover:bg-slate-100 font-semibold shadow-lg"
                    >
                      <Video className="w-5 h-5" />
                      Book Expert Session
                    </Button>
                    <Button 
                      size="lg"
                      variant="outline"
                      onClick={() => navigate('/expert-counseling')}
                      className="gap-2 border-white text-white hover:bg-white/20"
                    >
                      View Packages →
                    </Button>
                  </div>
                </div>
                
                <div className="hidden md:block">
                  <div className="bg-white/10 backdrop-blur rounded-2xl p-6 space-y-4">
                    <h4 className="font-semibold text-lg mb-4">Popular Packages</h4>
                    
                    <div className="bg-white/20 rounded-xl p-4 flex justify-between items-center">
                      <div>
                        <div className="font-medium">Quick Consultation</div>
                        <div className="text-sm opacity-80">15 min video call</div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold">₹299</div>
                        <div className="text-xs line-through opacity-60">₹499</div>
                      </div>
                    </div>
                    
                    <div className="bg-yellow-400/30 border border-yellow-400/50 rounded-xl p-4 flex justify-between items-center">
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          Standard Session
                          <Badge className="bg-yellow-400 text-yellow-900 text-xs">Popular</Badge>
                        </div>
                        <div className="text-sm opacity-80">30 min video call</div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold">₹599</div>
                        <div className="text-xs line-through opacity-60">₹999</div>
                      </div>
                    </div>
                    
                    <div className="bg-white/20 rounded-xl p-4 flex justify-between items-center">
                      <div>
                        <div className="font-medium">Premium Mentorship</div>
                        <div className="text-sm opacity-80">60 min + follow-up</div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold">₹1499</div>
                        <div className="text-xs line-through opacity-60">₹2499</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-4 py-16 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-3xl font-bold text-center text-slate-900 mb-12">Why Choose Our Predictor?</h3>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-slate-200 hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Trophy className="w-6 h-6 text-blue-600" />
                </div>
                <CardTitle>Comprehensive Database</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">Access data from 1000+ IITs, NITs, IIITs, GFTIs, and top private colleges</p>
              </CardContent>
            </Card>

            <Card className="border-slate-200 hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="w-6 h-6 text-cyan-600" />
                </div>
                <CardTitle>Historical Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">Analyze 5-year cutoff trends with AI-powered predictions</p>
              </CardContent>
            </Card>

            <Card className="border-slate-200 hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                  <Filter className="w-6 h-6 text-slate-600" />
                </div>
                <CardTitle>PDF Report & Email</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">Get detailed PDF report with predictions sent to your email</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-white py-8">
        <div className="container mx-auto px-4 text-center text-slate-600">
          <p>© 2024 JEE Predictor Pro. All rights reserved.</p>
        </div>
      </footer>

      {/* OTP Verification Modal */}
      <Dialog open={otpModal.open} onOpenChange={(open) => setOtpModal({ ...otpModal, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verify {otpModal.type === 'mobile' ? 'Mobile' : 'Email'}</DialogTitle>
            <DialogDescription>
              Enter the OTP sent to {otpModal.value}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              type="text"
              placeholder="Enter 6-digit OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              className="h-11"
              maxLength={6}
            />
            <div className="flex gap-2">
              <Button onClick={handleVerifyOTP} className="flex-1">Verify OTP</Button>
              <Button variant="outline" onClick={() => handleSendOTP(otpModal.type)}>Resend</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Login Prompt Modal */}
      <Dialog open={showLoginPrompt} onOpenChange={setShowLoginPrompt}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Your Predictions?</DialogTitle>
            <DialogDescription>
              Login to save your predictions for future reference and track your college options.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Button onClick={() => navigate('/login')} className="w-full">
              Login / Register
            </Button>
            <Button variant="outline" onClick={handleProceedWithoutLogin} className="w-full">
              Continue Without Login
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Existing User Login Modal */}
      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Welcome Back!</DialogTitle>
            <DialogDescription>
              We found an existing account with your details. Please login to continue.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="loginEmail">Email</Label>
              <Input
                id="loginEmail"
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                className="h-11"
                disabled
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="loginPassword">Password</Label>
              <Input
                id="loginPassword"
                type="password"
                placeholder="Enter your password"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                className="h-11"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleLoginSubmit} className="flex-1">
                Login
              </Button>
              <Button variant="outline" onClick={() => setShowLoginModal(false)} className="flex-1">
                Cancel
              </Button>
            </div>
            <p className="text-sm text-center text-slate-600">
              Don&apos;t remember your password? <button onClick={() => navigate('/login')} className="text-blue-600 hover:underline">Reset it here</button>
            </p>
          </div>
        </DialogContent>
      </Dialog>
      
      <Footer />
    </div>
  );
};

export default EnhancedHome;